import {
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Field,
    Input,
    Text,
    Textarea,
    Button,
    Card,
    CardHeader,
    Body1,
    Caption1,
    MessageBar,
    MessageBarBody,
    Tooltip,
} from '@fluentui/react-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postCustomerMessage } from '../../../store/features/customerUpdates/customerUpdates.api';
import { validateLength } from '../../../utils/validators';
import { useNewCustomerMessageDialogStyles } from './NewCustomerMessageDialog.styles';
import {
    SendPerson24Regular,
    Add24Regular,
    Edit24Regular,
    Delete24Regular,
    Warning24Regular,
} from '@fluentui/react-icons';
import { CloseDialogButton } from '../../../controls/Buttons/CloseButton';
import SubmitButton from '../../../controls/Buttons/SubmitButton';
import { useAppContext } from '../../../model';
import useFormState, { FieldDefinition } from '../../../hooks/useFormState';
import { newUuid } from '../../../utils/uuid';
import { useDialogStyles } from '../../../hooks/useDialog';

interface CustomerData {
    key: string;
    firstName: string;
    lastName: string;
    seatNumber: string;
    cabin: string;
    frequentFlyerTier: string;
    pnr: string;
}

const useNewCustomerMessageForm = () => {
    const { t } = useTranslation();

    const fieldDefinitions = useMemo(
        () =>
            [
                {
                    key: 'firstName',
                    label: t('CustomerMessage_FirstName'),
                    validator: (val: string) =>
                        validateLength(val, { min: 1, max: 64 })
                            ? null
                            : t('CustomerMessage_FirstName_Validation'),
                    required: true,
                },
                {
                    key: 'lastName',
                    label: t('CustomerMessage_LastName'),
                    validator: (val: string) =>
                        validateLength(val, { min: 1, max: 64 })
                            ? null
                            : t('CustomerMessage_LastName_Validation'),
                    required: true,
                },
                {
                    key: 'seatNumber',
                    label: t('CustomerMessage_SeatNumber'),
                    validator: (val: string) =>
                        validateLength(val, { min: 1, max: 10 })
                            ? null
                            : t('CustomerMessage_SeatNumber_Validation'),
                    required: true,
                    valueTransformer: (value) => value.toUpperCase(),
                },
                {
                    key: 'cabin',
                    label: t('CustomerMessage_Cabin'),
                    validator: (val: string) =>
                        validateLength(val, { max: 1 }, false)
                            ? null
                            : t('CustomerMessage_Cabin_Validation'),
                },
                {
                    key: 'frequentFlyerTier',
                    label: t('CustomerMessage_FrequentFlyerTier'),
                    validator: (val: string) =>
                        validateLength(val, { max: 50 }, false)
                            ? null
                            : t('CustomerMessage_FrequentFlyerTier_Validation'),
                },
                {
                    key: 'pnr',
                    label: t('CustomerMessage_Pnr'),
                    validator: (val: string) =>
                        validateLength(val, { max: 50 }, false)
                            ? null
                            : t('CustomerMessage_Pnr_Validation'),
                },
            ] as const satisfies readonly FieldDefinition[],
        [t]
    );

    return useFormState(fieldDefinitions);
};

interface CustomerListItemProps {
    customer: CustomerData;
    onEdit?: (customer: CustomerData) => void;
    onDelete?: (seatNumber: string) => void;
}

const CustomerListItem: React.FC<CustomerListItemProps> = ({
    customer,
    onEdit,
    onDelete,
}) => {
    const { t } = useTranslation();
    const styles = useNewCustomerMessageDialogStyles();

    return (
        <Card className={styles.customerCard}>
            <CardHeader
                header={
                    <Body1>
                        {customer.firstName} {customer.lastName}
                    </Body1>
                }
                description={
                    <Caption1>
                        {t('CustomerMessage_SeatNumber')}: {customer.seatNumber}
                        {customer.cabin &&
                            ` | ${t('CustomerMessage_Cabin')}: ${customer.cabin}`}
                    </Caption1>
                }
                action={
                    <div className={styles.customerActions}>
                        {onEdit && (
                            <Tooltip
                                relationship="label"
                                content={t('CustomerMessage_EditCustomer')}
                            >
                                <Button
                                    appearance="subtle"
                                    icon={<Edit24Regular />}
                                    onClick={() => onEdit(customer)}
                                    size="small"
                                    aria-label="Edit customer"
                                />
                            </Tooltip>
                        )}
                        {onDelete && (
                            <Tooltip
                                relationship="label"
                                content={t('CustomerMessage_DeleteCustomer')}
                            >
                                <Button
                                    appearance="subtle"
                                    icon={<Delete24Regular />}
                                    onClick={() =>
                                        onDelete(customer.seatNumber)
                                    }
                                    size="small"
                                    aria-label="Delete customer"
                                />
                            </Tooltip>
                        )}
                    </div>
                }
            />
        </Card>
    );
};

interface CustomerListProps {
    customers: CustomerData[];
    onEdit?: (customer: CustomerData) => void;
    onDelete?: (id: string) => void;
    title?: string;
    listClassName?: string;
}

const CustomerList: React.FC<CustomerListProps> = ({
    customers,
    onEdit,
    onDelete,
    title,
    listClassName,
}) => {
    const { t } = useTranslation();
    const styles = useNewCustomerMessageDialogStyles();

    if (customers.length === 0) {
        return (
            <div className={styles.emptyCustomerList}>
                <Caption1>{t('CustomerMessage_NoCustomersAdded')}</Caption1>
            </div>
        );
    }

    return (
        <div className={styles.customerList}>
            <Text weight="semibold" className={styles.customerListTitle}>
                {title ||
                    `${t('CustomerMessage_AddedCustomers')} (${customers.length})`}
            </Text>
            <div className={listClassName || styles.customerListItems}>
                {customers.map((customer) => (
                    <CustomerListItem
                        key={customer.seatNumber}
                        customer={customer}
                        onEdit={onEdit}
                        onDelete={onDelete}
                    />
                ))}
            </div>
        </div>
    );
};

interface MessageFieldProps {
    value: string;
    onChange?: (value: string) => void;
    validationMessage?: string | null;
    readOnly?: boolean;
    required?: boolean;
    placeholder?: string;
}

const MessageField: React.FC<MessageFieldProps> = ({
    value,
    onChange,
    validationMessage,
    placeholder,
}) => {
    const { t } = useTranslation();
    const styles = useNewCustomerMessageDialogStyles();

    return onChange ? (
        <div className={styles.messageSection}>
            <Field
                label={
                    <Text weight="semibold">
                        {t('CustomerMessage_Message')}
                    </Text>
                }
                validationMessage={validationMessage || undefined}
                required
                className={styles.field}
            >
                <Textarea
                    value={value}
                    onChange={(_, data) => onChange?.(data.value)}
                    placeholder={placeholder}
                />
            </Field>
        </div>
    ) : (
        <div className={styles.confirmationSection}>
            <Text weight="semibold" className={styles.confirmationLabel}>
                {t('CustomerMessage_Message')}
            </Text>
            <Card className={styles.confirmationMessageCard}>
                <Text>{value}</Text>
            </Card>
        </div>
    );
};

const ConfirmationView: React.FC<{
    customers: CustomerData[];
    message: string;
    onBack: () => void;
    onConfirm: () => void;
    isSubmitting: boolean;
}> = ({ customers, message, onBack, onConfirm, isSubmitting }) => {
    const { t } = useTranslation();
    const styles = useNewCustomerMessageDialogStyles();

    return (
        <div className={styles.confirmationView}>
            <Text weight="semibold" className={styles.sectionTitle}>
                {t('CustomerMessage_ConfirmTitle')}
            </Text>

            <div className={styles.confirmationSection}>
                <CustomerList
                    customers={customers}
                    title={`${t('CustomerMessage_Recipients')} (${customers.length})`}
                    listClassName={styles.confirmationCustomerList}
                />
            </div>

            <MessageField value={message} readOnly />

            <div className={styles.formActions}>
                <Button
                    appearance="primary"
                    icon={<SendPerson24Regular />}
                    onClick={onConfirm}
                    disabled={isSubmitting}
                >
                    {t('CustomerMessage_ConfirmSend')}
                </Button>
                <Button
                    appearance="subtle"
                    onClick={onBack}
                    disabled={isSubmitting}
                >
                    {t('CustomerMessage_BackToEdit')}
                </Button>
            </div>
        </div>
    );
};

type DialogView = 'list' | 'form' | 'confirmation';

export interface NewCustomerMessageDialogProps {
    open: boolean;
    flightTeamId: string;
    onOpenChange: (open: boolean) => void;
}

const CustomerForm: React.FC<{
    formState: ReturnType<typeof useNewCustomerMessageForm>;
    onSave: () => void;
    onCancel: () => void;
    editingCustomer: CustomerData | null;
    duplicateWarning: string | null;
}> = ({ formState, onSave, onCancel, editingCustomer, duplicateWarning }) => {
    const { t } = useTranslation();
    const styles = useNewCustomerMessageDialogStyles();

    const { fields, metadata, labels, errors, values, updateField, isValid } =
        formState;

    const handleSave = () => {
        if (isValid) {
            onSave();
        }
    };

    return (
        <div className={styles.customerForm}>
            <Text weight="semibold" className={styles.sectionTitle}>
                {editingCustomer
                    ? t('CustomerMessage_EditCustomer')
                    : t('CustomerMessage_AddCustomer')}
            </Text>
            {duplicateWarning && (
                <MessageBar
                    intent="warning"
                    icon={<Warning24Regular />}
                    className={styles.duplicateWarning}
                >
                    <MessageBarBody>{duplicateWarning}</MessageBarBody>
                </MessageBar>
            )}
            {fields.map((key) => {
                const meta = metadata[key];

                return (
                    <Field
                        key={key}
                        label={<Text weight="semibold">{labels[key]}</Text>}
                        validationMessage={errors[key]}
                        required={meta.required}
                        className={styles.field}
                    >
                        <Input
                            value={values[key]}
                            onChange={(_, data) => updateField(key, data.value)}
                        />
                    </Field>
                );
            })}
            <div className={styles.formActions}>
                <Button
                    appearance="primary"
                    icon={
                        editingCustomer ? <Edit24Regular /> : <Add24Regular />
                    }
                    onClick={handleSave}
                    disabled={!isValid}
                >
                    {editingCustomer
                        ? t('CustomerMessage_UpdateCustomer')
                        : t('CustomerMessage_AddCustomer')}
                </Button>
                <Button appearance="subtle" onClick={onCancel}>
                    {t('CustomerMessage_CancelEdit')}
                </Button>
            </div>
        </div>
    );
};

const NewCustomerMessageDialog: React.FC<NewCustomerMessageDialogProps> = ({
    open,
    flightTeamId,
    onOpenChange,
}) => {
    const { t } = useTranslation();
    const styles = useNewCustomerMessageDialogStyles();
    const dialogStyles = useDialogStyles();
    const { teamsContext } = useAppContext();

    const form = useNewCustomerMessageForm();
    const { values, validateAll, reset, isValid } = form;

    const [customers, setCustomers] = useState<CustomerData[]>([]);
    const [editingCustomer, setEditingCustomer] = useState<CustomerData | null>(
        null
    );
    const [currentView, setCurrentView] = useState<DialogView>('list');
    const [message, setMessage] = useState('');
    const [messageError, setMessageError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [duplicateWarning, setDuplicateWarning] = useState<string | null>(
        null
    );

    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            reset();
            setCustomers([]);
            setEditingCustomer(null);
            setCurrentView('list');
            setMessage('');
            setMessageError(null);
            setIsSubmitting(false);
            setDuplicateWarning(null);
        }
    }, [open, reset]);

    // Check for duplicate seat number
    useEffect(() => {
        if (currentView !== 'form') {
            setDuplicateWarning(null);
            return;
        }

        const seatNumber = values.seatNumber?.trim();
        if (!seatNumber) {
            setDuplicateWarning(null);
            return;
        }

        const existingCustomer = customers.find(
            (c) => c.seatNumber === seatNumber
        );
        if (
            existingCustomer &&
            (!editingCustomer || editingCustomer.key !== existingCustomer.key)
        ) {
            setDuplicateWarning(
                t('CustomerMessage_DuplicateSeatWarning', {
                    seatNumber: existingCustomer.seatNumber,
                    firstName: existingCustomer.firstName,
                    lastName: existingCustomer.lastName,
                })
            );
        } else {
            setDuplicateWarning(null);
        }
    }, [values.seatNumber, customers, currentView, editingCustomer, t]);

    // Validate message
    const validateMessage = useCallback(
        (value: string) => {
            const isValid = validateLength(value, { min: 1, max: 1000 });
            setMessageError(
                isValid ? null : t('CustomerMessage_Message_Validation')
            );
            return isValid;
        },
        [t]
    );

    const handleMessageChange = useCallback(
        (value: string) => {
            setMessage(value);
            validateMessage(value);
        },
        [validateMessage]
    );

    // View change handler
    const changeView = useCallback(
        (view: DialogView, customer?: CustomerData) => {
            switch (view) {
                case 'list':
                    // Returning to list - clean up form state
                    setEditingCustomer(null);
                    reset();
                    setDuplicateWarning(null);
                    setCurrentView('list');
                    break;

                case 'form':
                    if (customer) {
                        // Editing existing customer
                        setEditingCustomer(customer);
                        setDuplicateWarning(null);
                        reset({
                            firstName: customer.firstName,
                            lastName: customer.lastName,
                            seatNumber: customer.seatNumber,
                            cabin: customer.cabin,
                            frequentFlyerTier: customer.frequentFlyerTier,
                            pnr: customer.pnr,
                        });
                    } else {
                        // Adding new customer
                        setEditingCustomer(null);
                        reset();
                        setDuplicateWarning(null);
                    }
                    setCurrentView('form');
                    break;

                case 'confirmation':
                    // Show confirmation - validate first
                    if (customers.length === 0 || !validateMessage(message)) {
                        return;
                    }
                    setCurrentView('confirmation');
                    break;
            }
        },
        [reset, customers.length, message, validateMessage]
    );

    // Add or update customer
    const handleAddOrUpdateCustomer = useCallback(() => {
        if (!isValid || !validateAll()) {
            return;
        }

        const customerData: CustomerData = {
            key: editingCustomer?.key ?? newUuid(),
            firstName: values.firstName,
            lastName: values.lastName,
            seatNumber: values.seatNumber,
            cabin: values.cabin,
            frequentFlyerTier: values.frequentFlyerTier,
            pnr: values.pnr,
        };

        setCustomers((prev) => [
            ...prev.filter(
                (c) =>
                    c.key !== editingCustomer?.key &&
                    c.seatNumber !== customerData.seatNumber
            ),
            customerData,
        ]);

        // Reset form and return to list view
        setEditingCustomer(null);
        reset();
        changeView('list');
    }, [isValid, validateAll, values, editingCustomer, reset, changeView]);

    // Delete customer
    const handleDeleteCustomer = useCallback((seatNumber: string) => {
        setCustomers((prev) => prev.filter((c) => c.seatNumber !== seatNumber));
    }, []);

    // Submit message to all customers
    const onSubmit = useCallback(() => {
        if (
            !flightTeamId ||
            customers.length === 0 ||
            !validateMessage(message) ||
            isSubmitting
        ) {
            return;
        }

        setIsSubmitting(true);

        postCustomerMessage({
            flightTeamId: flightTeamId,
            customers: customers.map((c) => ({
                firstName: c.firstName,
                lastName: c.lastName,
                cabin: c.cabin,
                seatNumber: c.seatNumber,
                frequentFlyerTier: c.frequentFlyerTier,
                pnr: c.pnr,
            })),
            message: message,
            groundTeamDisplayName: teamsContext.team?.displayName ?? null,
        }).then(() => {
            onOpenChange(false);
        });
    }, [
        flightTeamId,
        customers,
        message,
        validateMessage,
        isSubmitting,
        onOpenChange,
        teamsContext,
    ]);

    const canSubmit =
        customers.length > 0 && message.trim().length > 0 && !messageError;

    return flightTeamId ? (
        <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
            <DialogSurface className={styles.dialogSurface}>
                <DialogBody>
                    <DialogTitle
                        className={dialogStyles.title}
                        action={<CloseDialogButton />}
                    >
                        <span className={dialogStyles.titleIcon}>
                            <SendPerson24Regular />
                        </span>
                        <span>{t('CustomerMessage_Title')}</span>
                    </DialogTitle>
                    <DialogContent className={styles.dialogContent}>
                        {currentView === 'confirmation' && (
                            <ConfirmationView
                                customers={customers}
                                message={message}
                                onBack={() => changeView('list')}
                                onConfirm={onSubmit}
                                isSubmitting={isSubmitting}
                            />
                        )}
                        {currentView === 'form' && (
                            <CustomerForm
                                formState={form}
                                onSave={handleAddOrUpdateCustomer}
                                onCancel={() => changeView('list')}
                                editingCustomer={editingCustomer}
                                duplicateWarning={duplicateWarning}
                            />
                        )}
                        {currentView === 'list' && (
                            <>
                                <CustomerList
                                    customers={customers}
                                    onEdit={(customer) =>
                                        changeView('form', customer)
                                    }
                                    onDelete={handleDeleteCustomer}
                                />

                                <Button
                                    appearance="primary"
                                    icon={<Add24Regular />}
                                    onClick={() => changeView('form')}
                                    className={styles.addCustomerButton}
                                >
                                    {t('CustomerMessage_AddCustomer')}
                                </Button>

                                <MessageField
                                    value={message}
                                    onChange={handleMessageChange}
                                    validationMessage={messageError}
                                    required
                                    placeholder={t(
                                        'CustomerMessage_MessagePlaceholder'
                                    )}
                                />
                            </>
                        )}
                    </DialogContent>
                    <DialogActions position="end">
                        {currentView === 'list' && (
                            <SubmitButton
                                onSubmit={() => changeView('confirmation')}
                                disabled={!canSubmit || isSubmitting}
                                isSubmitting={isSubmitting}
                            />
                        )}
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    ) : null;
};

export default NewCustomerMessageDialog;
